import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';

const T = { tx3: "#9CA3AF" };

/**
 * Shared modal shell. Encapsulates the portal, backdrop, centered popup,
 * header (uppercase title + close X), animations (feIn / feOut / feBgIn /
 * feBgOut), Esc-to-close, click-outside-to-close, and the 180ms fade-out
 * before unmount.
 *
 * Extracted from src/components/FieldEditor.js (Commit J). Visual no-op —
 * styling, timings, and lifecycle are preserved verbatim so the existing
 * Industry/Source/etc. picker on contact records renders identically.
 *
 * Props:
 *   open      bool      whether the modal is visible
 *   title     string    rendered uppercase in the header
 *   width     number    popup width in px (default 320)
 *   onClose   fn        called on Esc, backdrop click, or close-X click
 *   footer    JSX|null  optional content rendered in a footer row at the
 *                       bottom of the popup (right-aligned, 14px gap above
 *                       the body, 8px gap between footer items)
 *   children  JSX       body content
 */
export default function Modal({ open, title, width = 320, onClose, footer = null, children }) {
  const [mounted, setMounted] = useState(open);
  const [closing, setClosing] = useState(false);

  // open transitions are the only trigger we care about. `mounted` is
  // intentionally omitted from deps — the effect itself updates it and
  // re-running on its own state change would loop. (The project's ESLint
  // config has react-hooks/exhaustive-deps disabled globally, so no
  // disable comment is needed here.)
  useEffect(() => {
    if (open) {
      setMounted(true);
      setClosing(false);
    } else if (mounted) {
      setClosing(true);
      const t = setTimeout(() => setMounted(false), 180);
      return () => clearTimeout(t);
    }
  }, [open]);

  useEffect(() => {
    if (!mounted) return;
    const handler = (e) => { if (e.key === "Escape") onClose?.(); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [mounted, onClose]);

  if (!mounted) return null;

  return ReactDOM.createPortal(
    <div style={{ position: "fixed", inset: 0, zIndex: 99999, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <style>{`
        @keyframes feIn{from{opacity:0;transform:scale(0.92) translateY(8px)}to{opacity:1;transform:scale(1) translateY(0)}}
        @keyframes feOut{from{opacity:1;transform:scale(1) translateY(0)}to{opacity:0;transform:scale(0.95) translateY(4px)}}
        @keyframes feBgIn{from{opacity:0}to{opacity:1}}
        @keyframes feBgOut{from{opacity:1}to{opacity:0}}
      `}</style>

      {/* Backdrop — click anywhere outside the popup closes */}
      <div
        onClick={onClose}
        style={{
          position: "absolute", inset: 0,
          background: "rgba(0,0,0,0.35)",
          backdropFilter: "blur(4px)",
          animation: closing ? "feBgOut .18s ease forwards" : "feBgIn .2s ease",
        }}
      />

      {/* Centered popup */}
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          position: "relative",
          width, padding: "20px 22px",
          background: "#33363D", borderRadius: 18,
          border: "1px solid rgba(217,219,221,0.12)",
          boxShadow: "0 24px 64px rgba(0,0,0,0.50), 0 0 0 1px rgba(217,219,221,0.05)",
          animation: closing ? "feOut .18s ease forwards" : "feIn .25s cubic-bezier(.16,1,.3,1)",
        }}
      >
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: T.tx3, letterSpacing: ".06em", textTransform: "uppercase" }}>
            {title}
          </div>
          <button
            onClick={onClose}
            style={{
              width: 24, height: 24, borderRadius: 7, border: "1px solid rgba(217,219,221,0.10)",
              background: "rgba(255,255,255,0.04)", color: T.tx3, cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13,
            }}
          >×</button>
        </div>

        {/* Body */}
        {children}

        {/* Footer */}
        {footer && (
          <div style={{ display: "flex", gap: 8, marginTop: 14, justifyContent: "flex-end" }}>
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
